import styled, { css } from 'styled-components'
import { Advert } from '@chegg-wt/core/components/Advert'
import { font, colors, spacing, breakpoints } from '@chegg-ui/theme'
import fanta from '@chegg-wt/core/theme/fanta/theme'
import { LayoutAlignProps } from '@chegg-wt/core/types/layout'
import { AspiraStack } from '@chegg-wt/core/theme/fanta/fonts'
import {
  adjust,
  smallLayout,
  mediumLayout,
  largeLayout,
} from '@chegg-wt/core/theme/theme'

// portal for the viewpage checkbox menu to be drawn in
export const CheckboxMenu = styled.div`
  width: 100%;
`

// apply full width and height background color to video player area
export const Background = styled.div`
  background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor20};
`

// a wrapper with the correct amount of margin on the right side so that the fixed-position ad sidebar doesn't overlap content
export const AdMarginWrapper = styled.div<LayoutAlignProps>`
  @media (min-width: ${(props) => smallLayout(props.theme)}) {
    margin-right: 310px;
  }

  @media (min-width: ${(props) => mediumLayout(props.theme)}) {
    width: ${({ isFullWidth = false }) => (isFullWidth ? '100%' : 'unset')};
    margin-right: ${(props) => (props.layoutHRCenter ? '100px' : '400px')};
  }
`

export const AdvertWrapper = styled.div`
  position: relative;
`
// the area containing the contents above the video player
export const Contents = styled.div`
  max-width: 900px;
  padding: 2px 32px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;

  @media (max-width: ${(props) => smallLayout(props.theme)}) {
    padding: 2px 16px;
  }
`

// the video player needs to be hidden on some layouts
export const VideoAdvert = styled(Advert)`
  width: 100%;
  height: 287px; // kind of random but this is the height of the video box including 2px border

  && {
    z-index: 300;
  }
  // hide video on layouts < 1200px wide
  @media (max-width: ${({ theme }) => adjust(mediumLayout(theme), -1)}) {
    // !important because this style is not as specific as some ads styling that sets display to block
    display: none !important;
  }
`

export const T1Advert = styled(Advert)`
  @media (min-width: ${({ theme }) => mediumLayout(theme)}) {
    && {
      display: none;
    }
  }
`

// the ad sidebar positioned to the right side
export const SideView = styled.div`
  position: absolute;
  right: 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px 66px;
  z-index: 400;

  // Creates a gap between ads. used instead of 'gap' property for safari compatibility
  & > * + * {
    margin-top: 20px;
  }

  @media (max-width: ${({ theme }) => adjust(largeLayout(theme), -1)}) {
    padding: 15px 30px;
  }

  @media (max-width: ${({ theme }) => adjust(mediumLayout(theme), -1)}) {
    padding-left: 10px;
    padding-right: 0;
  }

  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    display: none;
  }
`

export const StickySideView = styled.div<{
  isR3SideviewFixed?: boolean
  topBanner?: boolean
}>`
  top: 0px;
  position: fixed;
  ${({ isR3SideviewFixed, topBanner = false }) => {
    if (typeof isR3SideviewFixed === 'boolean' && !isR3SideviewFixed) {
      return css`
        top: ${topBanner ? '410px' : '270px'};
        position: absolute;
      `
    }
    if (typeof isR3SideviewFixed === 'undefined') {
      return css`
        top: 52px;
      `
    }
  }}
  right: 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px 66px;
  z-index: 300;

  // Creates a gap between ads. used instead of 'gap' property for safari compatibility
  & > * + * {
    margin-top: 20px;
  }

  @media (max-width: ${({ theme }) => adjust(largeLayout(theme), -1)}) {
    padding: 15px 30px;
  }

  @media (max-width: ${({ theme }) => adjust(mediumLayout(theme), -1)}) {
    padding-left: 10px;
    padding-right: 0;
  }

  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    display: none;
  }
`

// the area that contains the fixed position bottom advertisement and view page checkbox menu.
// width adjusted so that the contents are not overlapped by ad sideframe and docked video player
export const BottomTray = styled.div<{ showLvp: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: fixed;
  bottom: 0px;
  z-index: 400;
  width: ${(props) => (props.showLvp ? 'calc(100% - 410px)' : '100%')};

  @media (max-width: ${({ theme }) => adjust(smallLayout(theme), -1)}) {
    width: 100%;
  }

  @media (min-width: ${({ theme }) => smallLayout(theme)}) and (max-width: ${({
      theme,
    }) => adjust(mediumLayout(theme), -1)}) {
    width: ${(props) => (props.showLvp ? 'calc(100% - 340px)' : '100%')};
  }

  @media (min-width: ${({ theme }) => mediumLayout(theme)}) and (max-width: ${({
      theme,
    }) => adjust(largeLayout(theme), -1)}) {
    width: ${(props) => (props.showLvp ? 'calc(100% - 420px)' : '100%')};
  }
`

// TODO: this should be moved to a better place for sharing
type PageHeaderProps = {
  fontSizeOverride?: string
}
export const PageHeader = styled.h1.attrs({
  role: 'heading',
  'aria-level': 1,
})<PageHeaderProps>`
  && {
    ${AspiraStack};
    font-weight: ${fanta.font.weight.bold};
    color: ${colors.dark};
    margin: ${fanta.spacing.fanta.lg} 0 6px 0;

    font-size: ${({ fontSizeOverride }) => fontSizeOverride || font.size.px42};
    line-height: 44px;
    display: inline;

    // 'font.size.xxxl' for smaller breakpoints
    @media (max-width: 899px) {
      font-size: 2.25rem;
      line-height: 40px;
    }

    @media (max-width: 599px) {
      font-size: 1.875rem;
      line-height: 34px;
    }
  }
`
export const PageHeaderWrapper = styled.div`
  margin: 24px 0;
`

export const MainWrapper = styled.main.attrs({
  role: 'main',
})``

export const WritingAssistantHomeBody = styled.div`
  padding: ${spacing.px32} ${spacing.px16};
  max-width: 1200px;
  margin: 0 auto;

  @media only screen and (min-width: ${breakpoints.md.min}) {
    padding: ${spacing.px24} ${spacing.px16};
  }
  @media only screen and (min-width: ${breakpoints.xl.min}) {
    padding: ${spacing.px48} 0;
  }
`
